import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Eye, Megaphone, Trophy, Users } from "lucide-react";

const PILLARS = [
  { icon: Trophy, label: "Student Recognition" },
  { icon: Megaphone, label: "Event Promotion" },
  { icon: Eye, label: "School Culture" },
  { icon: Users, label: "Family Communication" },
];

/**
 * Homepage Visibility Check™ section.
 * Placed immediately above the "How It Works" section per build brief.
 * Note: This is a navigation/discovery link, not a sales CTA — the only
 * sales CTA on the site remains "Request a Quote".
 */
export default function HomeVisibilityCheck() {
  return (
    <section
      id="home-visibility-check"
      data-testid="home-visibility-check-section"
      className="relative bg-white py-24 md:py-32 border-y border-slate-200 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute -top-32 -right-32 w-96 h-96 bg-teal/5 rounded-full blur-3xl"
      />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-teal mb-4">
              School leadership resource
            </div>
            <h2
              data-testid="home-vc-headline"
              className="font-serif text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-black tracking-tight text-navy leading-[1.08]"
            >
              How Visible Is{" "}
              <span className="text-teal">Your School?</span>
            </h2>

            <div className="mt-8 space-y-4 text-base sm:text-lg text-slate-600 leading-relaxed max-w-2xl">
              <p>
                The most effective schools don't just achieve great things.
                <br className="hidden sm:block" />
                <strong className="text-navy">
                  They make those achievements visible.
                </strong>
              </p>
              <p>
                From student recognition and school culture to event promotion
                and family communication, visibility plays a critical role in
                engagement.
              </p>
              <p>
                Take the Visibility Check<sup>™</sup> to discover opportunities
                to strengthen communication, recognition, and engagement
                throughout your school.
              </p>
            </div>

            <div className="mt-10">
              <Link
                data-testid="home-vc-cta-link"
                to="/visibility-check"
                className="group inline-flex items-center justify-center gap-2 bg-navy hover:bg-[#091638] text-white font-bold px-7 py-3.5 rounded-lg shadow-md hover:shadow-xl transition-all duration-200 hover:-translate-y-0.5"
              >
                Take the Visibility Check<sup>™</sup>
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </Link>
              <p className="mt-4 text-sm text-slate-500">
                Free · 5-minute assessment · Personalized report
              </p>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div className="bg-[#F4F6F4] border border-slate-200 rounded-2xl p-8 md:p-10">
              <div className="cmyk-ribbon h-1 w-16 mb-8 rounded-full" aria-hidden />
              <p className="text-[10px] uppercase tracking-[0.18em] font-bold text-teal mb-6">
                What it covers
              </p>
              <ul className="space-y-4">
                {PILLARS.map((p) => {
                  const Icon = p.icon;
                  return (
                    <li
                      key={p.label}
                      className="flex items-center gap-4"
                    >
                      <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-navy text-white flex items-center justify-center">
                        <Icon className="w-5 h-5" strokeWidth={2} />
                      </span>
                      <span className="font-serif text-base sm:text-lg font-bold text-navy">
                        {p.label}
                      </span>
                    </li>
                  );
                })}
              </ul>
              <p className="mt-8 pt-6 border-t border-slate-200 text-sm text-slate-500 italic">
                Plus Staff Empowerment — 5 dimensions in total.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
